import { ReactNode, useEffect, useRef } from 'react'

import { IoIosClose } from 'react-icons/io'

import { Button } from '../button'
import { Loader } from '../loader'

export enum ModalVariation {
  default = 'default',
  success = 'success',
  info = 'info',
  warning = 'warning',
  danger = 'danger',
}

export interface ModalProps {
  show?: boolean
  onHide?: () => void
  title: ReactNode
  bodyContent: ReactNode
  variation?: keyof typeof ModalVariation
  footerButtons?: {
    variant?: keyof typeof ModalVariation
    class?: string
    label: string
    onClick?: () => void
    disabled?: boolean
    isLoading?: boolean
    ariaLabel?: string
    useModalVariant?: boolean
  }[]
  className?: string
  primary?: boolean
  success?: boolean
  info?: boolean
  warning?: boolean
  danger?: boolean
  hideFooter?: boolean
  maxWidth?: string
}

const getButtonProps = (
  button: { label: string; variant?: keyof typeof ModalVariation; useModalVariant?: boolean },
  modalProps: ModalProps
): keyof typeof ModalVariation | undefined => {
  if (button.variant) return button.variant

  if (button.useModalVariant) {
    if (modalProps.success) return 'success'
    if (modalProps.warning) return 'warning'
    if (modalProps.danger) return 'danger'
    if (modalProps.info) return 'info'

    return 'default'
  }
  return undefined
}

const variantClassMap: Record<keyof typeof ModalVariation, string> = {
  default: 'bg-primary',
  success: 'bg-success',
  info: 'bg-info',
  warning: 'bg-warning',
  danger: 'bg-error',
}

export default function Modal({
  show,
  onHide,
  maxWidth,
  title,
  bodyContent,
  footerButtons,
  hideFooter,
  // className,
  success,
  info,
  warning,
  danger,
  variation = 'default',
}: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = dialogRef.current

    if (!dialog) return

    if (show) {
      dialog.showModal()
    } else {
      dialog.close()
    }
  }, [show])

  const headerClass = `${variantClassMap[variation]} w-full text-base-100 px-6 py-2 flex justify-between items-center rounded-t-lg`

  useEffect(() => {
    const dialog = dialogRef.current

    if (!dialog || !onHide) return

    const handleCancel = (e: Event) => {
      e.preventDefault()
      onHide()
    }
    dialog.addEventListener('cancel', handleCancel)

    return () => {
      dialog.removeEventListener('cancel', handleCancel)
    }
  }, [show, onHide])

  const getButtonClass = (
    button: { useModalVariant?: boolean; class?: string },
    effectiveVariant?: keyof typeof ModalVariation
  ): string => {
    const baseClass = button.class || ''

    // If button doesn't use modal variant, it should be tertiary (gray)
    if (!button.useModalVariant) {
      return `${baseClass} button-tertiary-light`
    }

    // Apply appropriate variant class based on effectiveVariant
    if (effectiveVariant) {
      return `${baseClass} button-${effectiveVariant}`
    }

    return baseClass
  }
  const classes = ['modal-box', 'p-0', 'overflow-visible']
  return (
    <dialog ref={dialogRef} className="modal ">
      <div
        data-max-width={maxWidth}
        style={
          maxWidth
            ? {
                width: '95vw',
                maxWidth: `${maxWidth}`,
              }
            : {}
        }
        className={classes.join(' ')}
      >
        <div className={headerClass}>
          <h3 className="text-xl w-full">{title}</h3>
          <button aria-label="Close" className="cursor-pointer" type="button" onClick={onHide}>
            <IoIosClose className="p-0 h-8 w-8" />
          </button>
        </div>
        <div className="p-6 max-h-[600px] md:max-h-fit overflow-y-scroll">{bodyContent}</div>
        {!hideFooter && (
          <div className="modal-action">
            <div className="p-6 flex flex-row-reverse gap-4">
              {footerButtons &&
                footerButtons.map((button) => {
                  const effectiveVariant = getButtonProps(button, {
                    success,
                    info,
                    warning,
                    danger,
                    show,
                    onHide,
                    title,
                    bodyContent,
                  })
                  const buttonProps = effectiveVariant
                    ? {
                        default: effectiveVariant === ModalVariation.default,
                        success: effectiveVariant === ModalVariation.success,
                        info: effectiveVariant === ModalVariation.info,
                        warning: effectiveVariant === ModalVariation.warning,
                        danger: effectiveVariant === ModalVariation.danger,
                      }
                    : {}
                  return (
                    <Button
                      key={button.label}
                      isDisabled={button.disabled}
                      handleClick={button.onClick || (() => {})}
                      className={getButtonClass(button, effectiveVariant)}
                      // className={`${button.class || ''} ${getButtonClass(button, effectiveVariant)}`}
                      {...buttonProps}
                    >
                      <span className="relative inline-flex items-center justify-center whitespace-nowrap">
                        <span className={button.isLoading ? 'invisible' : ''}>{button.label}</span>
                        {button.isLoading ? (
                          <span className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
                            <Loader />
                          </span>
                        ) : null}
                      </span>
                    </Button>
                  )
                })}
            </div>
          </div>
        )}
      </div>
    </dialog>
  )
}
